import React, { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Database, ExternalLink, Layers, SearchX, Smartphone } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { PageShell, StatCard, fmtNum } from '../Integrations/integrationsUi';
import { Card, EmptyRow, ErrorNotice, INPUT_CLASS, Th } from '../Scheduler/schedulerUi';

/** A phone a seller could not find in the device picker and listed by hand. */
interface UnlistedDevice {
  key: string;
  brand: string;
  model: string;
  count: number;
  weeks: number;
  first_at: string | null;
  last_at: string | null;
  last_query: string | null;
  in_catalogue: boolean;
}

interface DeviceCatalogueReport {
  weeks: number;
  catalogue: { total: number; gsmarena: number; gsmchoice: number; mobiledokan: number };
  unlisted: UnlistedDevice[];
  open: number;
}

type CatalogueSource = 'gsmarena' | 'gsmchoice' | 'mobiledokan';

/** One phone as the device picker offers it, with the choices the listing form will show. */
interface CatalogueDevice {
  key: string;
  name: string;
  image: string;
  release_date: string;
  os: string;
  source: CatalogueSource;
  verified: boolean;
  superseded_by: string | null;
  refreshed: string | null;
  storage: string[];
  colours: string[];
}

interface CatalogueDeviceDetail extends CatalogueDevice {
  brand: string | null;
  model: string | null;
  device_id: number | null;
  source_url: string;
  summary: Record<string, string>;
  specification: { title: string; rows: { title: string; values: string[] }[] }[];
  pictures: string[];
  history: number;
}

interface BrowseResponse {
  total: number;
  page: number;
  per_page: number;
  devices: CatalogueDevice[];
}

interface CatalogueBrand {
  key: string;
  name: string;
  devices: number | null;
}

interface BrowseFilters {
  q: string;
  brand: string;
  source: string;
  flag: string;
  sort: string;
  page: number;
}

const BASE = '/api/admin/stores/catalog/device-catalogue';
const PER_PAGE = 50;

const SOURCE_LABEL: Record<CatalogueSource, string> = {
  gsmarena: 'GSMArena',
  gsmchoice: 'GSMchoice',
  mobiledokan: 'MobileDokan',
};

const SUMMARY_LABELS: [string, string][] = [
  ['release_date', 'Released'],
  ['os_type', 'Operating system'],
  ['display_size', 'Screen'],
  ['display_res', 'Resolution'],
  ['chipset', 'Chipset'],
  ['ram', 'Memory'],
  ['storage', 'Storage'],
  ['camera', 'Camera'],
  ['battery', 'Battery'],
  ['body', 'Body'],
];

const useCatalogueGet = <T,>(key: unknown[], path: string, enabled = true) => {
  const request = useClientStore((s) => s.request);
  const load = withErrorHandling(
    async (): Promise<unknown> => request<unknown>(`${BASE}${path}`),
    'Failed to load the device catalogue',
  );
  return useQuery({
    queryKey: ['device-catalogue', ...key],
    queryFn: load as unknown as () => Promise<T>,
    enabled,
    staleTime: 1000 * 60,
    placeholderData: keepPreviousData,
  });
};

const browsePath = (f: BrowseFilters) => {
  const params = new URLSearchParams({
    page: String(f.page),
    per_page: String(PER_PAGE),
    sort: f.sort,
  });
  if (f.q) params.set('q', f.q);
  if (f.brand) params.set('brand', f.brand);
  if (f.source) params.set('source', f.source);
  if (f.flag) params.set('flag', f.flag);
  return `/devices?${params.toString()}`;
};

const SourceBadge: React.FC<{ device: CatalogueDevice }> = ({ device }) => (
  <span className="inline-flex flex-wrap items-center gap-1">
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
        device.source === 'gsmarena' ? 'bg-gray-100 text-gray-700' : 'bg-blue-50 text-blue-700'
      }`}
    >
      {SOURCE_LABEL[device.source] ?? device.source}
    </span>
    {!device.verified && (
      <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
        Unverified
      </span>
    )}
    {device.superseded_by && (
      <span className="inline-flex rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
        Retired from search
      </span>
    )}
  </span>
);

const Chips: React.FC<{ values: string[]; empty: string }> = ({ values, empty }) =>
  values.length === 0 ? (
    <span className="text-amber-700">{empty}</span>
  ) : (
    <span className="flex flex-wrap gap-1">
      {values.map((v) => (
        <span
          key={v}
          className="rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-xs text-gray-700"
        >
          {v}
        </span>
      ))}
    </span>
  );

/** Everything the catalogue holds for one phone, and what a seller will be offered for it. */
const DevicePreview: React.FC<{ deviceKey: string | null; onClose: () => void }> = ({
  deviceKey,
  onClose,
}) => {
  const q = useCatalogueGet<CatalogueDeviceDetail>(
    ['device', deviceKey],
    `/device?key=${encodeURIComponent(deviceKey ?? '')}`,
    !!deviceKey,
  );
  const d = deviceKey && q.data?.key === deviceKey ? q.data : undefined;

  return (
    <Sheet open={!!deviceKey} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{d?.name ?? 'Device'}</SheetTitle>
          <SheetDescription>{deviceKey}</SheetDescription>
        </SheetHeader>

        {q.error ? (
          <div className="mt-4">
            <ErrorNotice error={q.error} onRetry={() => void q.refetch()} />
          </div>
        ) : !d ? (
          <div className="mt-4 space-y-3">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <div className="mt-4 space-y-5 text-sm">
            <div className="flex gap-4">
              {d.image ? (
                <img
                  src={d.image}
                  alt={d.name}
                  className="h-36 w-28 shrink-0 rounded-lg border border-gray-100 bg-white object-contain"
                />
              ) : (
                <div className="flex h-36 w-28 shrink-0 items-center justify-center rounded-lg border border-dashed border-gray-200 text-xs text-gray-400">
                  No image
                </div>
              )}
              <div className="min-w-0 space-y-2">
                <SourceBadge device={d} />
                <p className="text-gray-600">
                  {d.brand ?? '—'} · {d.model ?? '—'}
                </p>
                <p className="text-xs text-gray-500">
                  Last read from the source: {d.refreshed ?? 'unknown'}
                  {d.history > 0 ? ` · changed ${d.history} time${d.history === 1 ? '' : 's'}` : ''}
                </p>
                {d.source_url && (
                  <a
                    href={d.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline"
                  >
                    Open on {SOURCE_LABEL[d.source] ?? d.source}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-900">
                What a seller is offered
              </p>
              <div className="space-y-2">
                <div>
                  <p className="mb-1 text-xs text-gray-500">Storage and memory</p>
                  <Chips values={d.storage} empty="None: the seller types it in." />
                </div>
                <div>
                  <p className="mb-1 text-xs text-gray-500">Colour</p>
                  <Chips
                    values={d.colours}
                    empty="None: the seller picks from the general colour list."
                  />
                </div>
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Summary
              </p>
              <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1">
                {SUMMARY_LABELS.filter(([k]) => d.summary[k]).map(([k, label]) => (
                  <React.Fragment key={k}>
                    <dt className="text-gray-500">{label}</dt>
                    <dd className="text-gray-900">{d.summary[k]}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>

            {d.pictures.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Pictures
                </p>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {d.pictures.map((src) => (
                    <img
                      key={src}
                      src={src}
                      alt=""
                      loading="lazy"
                      className="h-28 w-auto shrink-0 rounded-md border border-gray-100 bg-white object-contain"
                    />
                  ))}
                </div>
              </div>
            )}

            {d.specification.map((section) => (
              <div key={section.title}>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {section.title}
                </p>
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-gray-100">
                    {section.rows.map((row, i) => (
                      <tr key={`${row.title}-${i}`}>
                        <td className="w-32 py-1 pr-3 align-top text-gray-500">
                          {row.title || '—'}
                        </td>
                        <td className="py-1 text-gray-900">{row.values.join(' · ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const BrowseTab: React.FC = () => {
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<BrowseFilters>({
    q: '',
    brand: '',
    source: '',
    flag: '',
    sort: 'name',
    page: 1,
  });
  const [openKey, setOpenKey] = useState<string | null>(null);

  // Search as the admin types, without a request per keystroke.
  useEffect(() => {
    const t = setTimeout(
      () => setFilters((f) => (f.q === search.trim() ? f : { ...f, q: search.trim(), page: 1 })),
      350,
    );
    return () => clearTimeout(t);
  }, [search]);

  const set = (patch: Partial<BrowseFilters>) => setFilters((f) => ({ ...f, page: 1, ...patch }));
  const listQ = useCatalogueGet<BrowseResponse>(['devices', filters], browsePath(filters));
  const brandsQ = useCatalogueGet<{ brands: CatalogueBrand[] }>(['brands'], '/brands');
  const d = listQ.data;
  const pages = d ? Math.max(1, Math.ceil(d.total / d.per_page)) : 1;

  return (
    <>
      {listQ.error ? (
        <ErrorNotice error={listQ.error} onRetry={() => void listQ.refetch()} />
      ) : null}
      <Card
        title="Catalogue"
        note={d ? `${fmtNum(d.total)} phone${d.total === 1 ? '' : 's'}` : undefined}
        flush
      >
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 p-3">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, e.g. tecno pop 20"
            aria-label="Search the catalogue"
            className={`${INPUT_CLASS} w-64`}
          />
          <select
            value={filters.brand}
            onChange={(e) => set({ brand: e.target.value })}
            aria-label="Brand"
            className={INPUT_CLASS}
          >
            <option value="">All brands</option>
            {(brandsQ.data?.brands ?? []).map((b) => (
              <option key={b.key} value={b.key}>
                {b.name}
              </option>
            ))}
          </select>
          <select
            value={filters.source}
            onChange={(e) => set({ source: e.target.value })}
            aria-label="Source"
            className={INPUT_CLASS}
          >
            <option value="">All sources</option>
            <option value="gsmarena">GSMArena</option>
            <option value="gsmchoice">GSMchoice</option>
            <option value="mobiledokan">MobileDokan</option>
          </select>
          <select
            value={filters.flag}
            onChange={(e) => set({ flag: e.target.value })}
            aria-label="Show"
            className={INPUT_CLASS}
          >
            <option value="">Every record</option>
            <option value="no_storage">No storage options</option>
            <option value="no_colours">No colour options</option>
            <option value="superseded">Retired from search</option>
          </select>
          <select
            value={filters.sort}
            onChange={(e) => set({ sort: e.target.value })}
            aria-label="Sort"
            className={INPUT_CLASS}
          >
            <option value="name">Name</option>
            <option value="recent">Newest added</option>
            <option value="refreshed">Last refreshed</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <Th>Phone</Th>
                <Th>Storage and memory</Th>
                <Th>Colours</Th>
                <Th>Released</Th>
                <Th>Source</Th>
                <Th>Refreshed</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {!d || d.devices.length === 0 ? (
                <EmptyRow cols={6} loading={listQ.isLoading}>
                  No phones match these filters.
                </EmptyRow>
              ) : (
                d.devices.map((device) => (
                  <tr
                    key={device.key}
                    onClick={() => setOpenKey(device.key)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenKey(device.key);
                        }}
                        className="flex items-center gap-3 text-left"
                      >
                        {device.image ? (
                          <img
                            src={device.image}
                            alt=""
                            loading="lazy"
                            className="h-10 w-8 shrink-0 rounded bg-white object-contain"
                          />
                        ) : (
                          <span className="h-10 w-8 shrink-0 rounded bg-gray-100" />
                        )}
                        <span className="font-medium text-gray-900 hover:underline">
                          {device.name}
                        </span>
                      </button>
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {device.storage.length ? (
                        device.storage.join(', ')
                      ) : (
                        <span className="text-amber-700">none</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {device.colours.length ? (
                        `${device.colours.length}: ${device.colours.slice(0, 3).join(', ')}${
                          device.colours.length > 3 ? '…' : ''
                        }`
                      ) : (
                        <span className="text-amber-700">none</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-600">{device.release_date || '—'}</td>
                    <td className="px-4 py-2">
                      <SourceBadge device={device} />
                    </td>
                    <td className="px-4 py-2 text-gray-600">{device.refreshed ?? '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2.5 text-sm text-gray-600">
          <span>
            Page {filters.page} of {fmtNum(pages)}
          </span>
          <span className="flex gap-2">
            <button
              type="button"
              disabled={filters.page <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
              className="rounded-lg border border-gray-200 px-3 py-1 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={filters.page >= pages}
              onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
              className="rounded-lg border border-gray-200 px-3 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </span>
        </div>
      </Card>
      <DevicePreview deviceKey={openKey} onClose={() => setOpenKey(null)} />
    </>
  );
};

const WEEK_OPTIONS = [4, 8, 26, 52];

const fmtDate = (value: string | null) => (value ? new Date(value).toLocaleDateString() : '—');

const useDeviceCatalogueQuery = (weeks: number) => {
  const request = useClientStore((s) => s.request);
  const load = withErrorHandling(
    async (): Promise<unknown> =>
      request<unknown>(`/api/admin/stores/catalog/device-catalogue?weeks=${weeks}`),
    'Failed to load the device catalogue',
  );
  return useQuery({
    queryKey: ['device-catalogue', weeks],
    queryFn: load as unknown as () => Promise<DeviceCatalogueReport>,
    staleTime: 1000 * 60,
    placeholderData: keepPreviousData,
  });
};

const DeviceCataloguePage: React.FC = () => {
  const [tab, setTab] = useState<'browse' | 'missing'>('browse');
  const [weeks, setWeeks] = useState(8);
  const q = useDeviceCatalogueQuery(weeks);
  const d = q.data;

  return (
    <PageShell
      title="Device catalogue"
      description="Every phone the device picker offers, with the storage and colour choices a seller sees, and the phones sellers looked for and could not find."
      actions={
        tab === 'missing' ? (
          <div className="inline-flex overflow-hidden rounded-lg border border-gray-200">
            {WEEK_OPTIONS.map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => setWeeks(w)}
                className={`px-3 py-1.5 text-xs font-medium ${
                  weeks === w ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {w} weeks
              </button>
            ))}
          </div>
        ) : undefined
      }
    >
      {q.error ? <ErrorNotice error={q.error} onRetry={() => void q.refetch()} /> : null}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Phones in the catalogue"
          value={d ? fmtNum(d.catalogue.total) : '—'}
          icon={<Smartphone className="h-5 w-5 text-blue-600" />}
          color="bg-blue-50"
        />
        <StatCard
          label="From GSMArena"
          value={d ? fmtNum(d.catalogue.gsmarena) : '—'}
          icon={<Database className="h-5 w-5 text-gray-600" />}
          color="bg-gray-100"
        />
        <StatCard
          label="Added from other sources"
          value={d ? fmtNum(d.catalogue.gsmchoice + d.catalogue.mobiledokan) : '—'}
          hint={
            d
              ? `${fmtNum(d.catalogue.gsmchoice)} GSMchoice, ${fmtNum(d.catalogue.mobiledokan)} MobileDokan`
              : undefined
          }
          icon={<Layers className="h-5 w-5 text-emerald-600" />}
          color="bg-emerald-50"
        />
        <StatCard
          label="Asked for and still missing"
          value={d ? fmtNum(d.open) : '—'}
          hint={`last ${weeks} weeks`}
          icon={<SearchX className="h-5 w-5 text-amber-600" />}
          color="bg-amber-50"
        />
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {(
          [
            ['browse', 'Browse'],
            ['missing', `Missing${d && d.open ? ` (${d.open})` : ''}`],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
              tab === id
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'browse' && <BrowseTab />}

      {tab === 'missing' && (
        <Card
          title="Phones sellers could not find"
          note="Recorded when a seller chooses My phone isn't listed and continues by hand."
          flush
        >
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <Th>Phone</Th>
                  <Th right>Times asked</Th>
                  <Th right>Weeks</Th>
                  <Th>First asked</Th>
                  <Th>Last asked</Th>
                  <Th>Last search</Th>
                  <Th>Catalogue</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {!d || d.unlisted.length === 0 ? (
                  <EmptyRow cols={7} loading={q.isLoading}>
                    No seller has reported a missing phone in the last {weeks} weeks.
                  </EmptyRow>
                ) : (
                  d.unlisted.map((u) => (
                    <tr key={u.key} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-900">
                        {u.brand} {u.model}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(u.count)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{u.weeks}</td>
                      <td className="px-4 py-2.5 text-gray-600">{fmtDate(u.first_at)}</td>
                      <td className="px-4 py-2.5 text-gray-600">{fmtDate(u.last_at)}</td>
                      <td className="px-4 py-2.5 text-gray-500">{u.last_query || '—'}</td>
                      <td className="px-4 py-2.5">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            u.in_catalogue
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-amber-50 text-amber-800'
                          }`}
                        >
                          {u.in_catalogue ? 'Added since' : 'Missing'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </PageShell>
  );
};

export default DeviceCataloguePage;
